import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Check } from '../../lib/types';

const STEPS = ['queued', 'processing', 'positions', 'rules', 'claims', 'label'] as const;
type Step = (typeof STEPS)[number];

/** Stepper shown while a document check runs (server reports `check.stage`; queued before the job starts). */
export function VerifyProgress({ check, since }: { check: Check | null; since: string | null }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const current: Step =
    !check || check.status === 'queued'
      ? 'queued'
      : ((check.stage as Step | null | undefined) ?? 'processing');
  const steps = STEPS.filter((s) => s !== 'claims' || !check?.scope || check.scope.includes('claims'));
  const idx = steps.indexOf(current);
  const start = since ? new Date(since).getTime() : now;
  const secs = Math.max(0, Math.round((now - start) / 1000));
  const elapsed = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3" role="status" aria-live="polite">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
        <span className="font-semibold text-blue-900">{t('review.progressTitle')}</span>
        <span className="font-mono text-xs text-blue-800">{elapsed}</span>
        <span className="ml-auto text-xs text-blue-800">{t('review.progressHint')}</span>
      </div>
      <ol className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {steps.map((s, i) => {
          const state = i < idx ? 'done' : i === idx ? 'active' : 'todo';
          return (
            <li
              key={s}
              className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs ${
                state === 'done'
                  ? 'border-emerald-200 bg-white text-emerald-800'
                  : state === 'active'
                    ? 'border-blue-400 bg-white font-semibold text-blue-900 shadow-sm'
                    : 'border-slate-200 bg-white/60 text-slate-500'
              }`}
            >
              <span
                className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                  state === 'done'
                    ? 'bg-emerald-600 text-white'
                    : state === 'active'
                      ? 'animate-pulse bg-blue-600 text-white'
                      : 'bg-slate-200 text-slate-600'
                }`}
              >
                {state === 'done' ? '✓' : i + 1}
              </span>
              {t(`review.step_${s}`)}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
